/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

/*import url and gateway methods */
import { getData, postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

//import images
import CommonHeader from "components/MarketplaceComponents/MarketplaceHeader/CommonHeader";
import { assetImages } from "constants";

import { useTranslation } from "react-i18next";
import SaveAddressModal from "../Modal/SaveAddressModal";

import currencyOptions from "data/Prod/Currency.json";
import languageOptions from "data/Prod/LanguageOptions.json";
import paymentOptions from "data/Prod/PaymentOptions.json";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";

const SettingsBody = () => {
  const token = localStorage.getItem("token");
  const [isLoading, setIsLoading] = useState(true);
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess");
  const { t } = useTranslation(); //for translation

  // --- Component State ---
  const [storeName, setStoreName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [currencyValue, setCurrencyValue] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState([]);
  const [languageValue, setLanguageValue] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);

  const [addressData, setAddressData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
    mapurl: "",
  });

  const [addressBlock, setAddressBlock] = useState([]);
  const [addressIndex, setAddressIndex] = useState(null);
  const [deletedAddressBlocks, setDeletedAddressBlocks] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  // --- State for Validation and Alerts ---
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // --- Form Validation ---
  const validateForm = () => {
    const newErrors = {};

    if (!storeName.trim()) {
      newErrors.storeName = "Store name is required.";
    }

    if (!contactEmail.trim()) {
      newErrors.contactEmail = "A valid contact email is required.";
    } else if (!/\S+@\S+\.\S+/.test(contactEmail)) {
      newErrors.contactEmail = "Email address is invalid.";
    }

    if (!contactNumber.trim()) {
      newErrors.contactNumber = "A contact phone number is required.";
    }

    if (!currencyValue || currencyValue.length === 0) {
      newErrors.currency = "At least one currency must be selected.";
    }

    if (!languageValue || languageValue.length === 0) {
      newErrors.language = "At least one language must be selected.";
    }

    if (selectedPaymentMethods.length === 0) {
      newErrors.paymentMethods =
        "At least one payment method must be selected.";
    }

    if (addressBlock.length === 0) {
      newErrors.addressBlock = "At least one warehouse address must be added.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- Event Handlers for Form Inputs ---

  const currencySelectionHandler = (val) => {
    setErrors((prev) => ({ ...prev, currency: "" })); // Clear validation on change
    setCurrencyValue(val);
    setSelectedCurrency(val.map((item) => item.value));
  };

  const languageSelectionHandler = (val) => {
    setErrors((prev) => ({ ...prev, language: "" })); // Clear validation on change
    setLanguageValue(val);
    setSelectedLanguages(val.map((item) => item.value));
  };

  const paymentSelectionHandler = (e) => {
    setErrors((prev) => ({ ...prev, paymentMethods: "" })); // Clear validation on change
    const { value, checked } = e.target;
    setSelectedPaymentMethods((prev) =>
      checked ? [...prev, value] : prev.filter((method) => method !== value),
    );
  };

  // --- Address Block Handlers ---
  const updateAddressBlock = (addressData, index) => {
    setAddressData(addressData);
    setAddressIndex(index);
  };

  const onDeleteAddressBlock = (index, item) => {
    const filteredBlock = [...addressBlock];
    filteredBlock.splice(index, 1);
    setAddressBlock(filteredBlock);

    if (item._id) {
      setDeletedAddressBlocks((prev) => [...prev, item._id]);
    }
  };

  const getAddressBlock = (addressList = []) => {
    const addressArr = addressList.map((address) => ({
      _id: address?._id,
      name: address?.name,
      address: address?.address,
      city: address?.city,
      state: address?.state,
      country: address?.country,
      zipcode: address?.zipcode,
      mapurl: address?.mapurl,
    }));
    setAddressBlock(addressArr);
  };

  // --- Data Fetching and Saving ---
  const getSettingsDetails = async () => {
    setIsLoading(true);
    try {
      const requestUrl =
        url.API_BASE_URL + url.API_GET_SETTING_DETAILS + `?token=${token}`;
      const response = await getData(requestUrl);

      if (response.status && response.data) {
        const data = response.data;
        setStoreName(data.storename || "");
        setContactEmail(data.contactemail || "");
        setContactNumber(data.contactphone || "");
        setSelectedPaymentMethods(data.paymentmethods || []);

        if (data.currency) {
          const filteredCurrencies = currencyOptions.filter((c) =>
            data.currency.includes(c.value),
          );
          currencySelectionHandler(filteredCurrencies);
        }
        if (data.languages) {
          const filterLanguages = languageOptions.filter((l) =>
            data.languages.includes(l.value),
          );
          languageSelectionHandler(filterLanguages);
        }
        if (data.warehouseaddresses) {
          getAddressBlock(data.warehouseaddresses);
        }
      }
    } catch (error) {
      setMessageType("error");
      setAlertMessage("Failed to load settings: " + error.message);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettingsHandler = async () => {
    if (!validateForm()) {
      // The inline error messages will appear, no need for a general alert
      return;
    }

    setIsSaving(true);
    try {
      let settingsData = {
        storename: storeName,
        contactemail: contactEmail,
        contactphone: contactNumber,
        currency: selectedCurrency,
        languages: selectedLanguages,
        paymentmethods: selectedPaymentMethods,
        addresses: addressBlock,
        deletedaddressblocks: deletedAddressBlocks,
      };

      const requestUrl =
        url.API_BASE_URL + url.API_CREATE_SETTING + `?token=${token}`;
      const response = await postData(requestUrl, settingsData);

      if (response.status) {
        setMessageType("success");
        setDeletedAddressBlocks([]);
        setTimeout(() => getSettingsDetails(), 1500);
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (error) {
      setMessageType("error");
      setAlertMessage("An unexpected error occurred: " + error.message);
    } finally {
      setIsSaving(false);
      setShowAlert(true);
    }
  };

  useEffect(() => {
    getSettingsDetails();
  }, []);

  const onAlertClose = () => {
    setShowAlert(false);
  };

  const currencyStyle = {
    control: (base, state) => ({
      ...base,
      borderColor: errors.currency ? "#dc3545" : "#ced4da",
      "&:hover": {
        borderColor: errors.currency ? "#dc3545" : "#ced4da",
      },
      borderRadius: "7px",
      padding: "3px",
      minHeight: "40px",
      boxShadow: "none",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#6c757d",
    }),
    input: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
  };

  const languageStyle = {
    control: (base, state) => ({
      ...base,
      borderColor: errors.language ? "#dc3545" : "#ced4da",
      "&:hover": {
        borderColor: errors.language ? "#dc3545" : "#ced4da",
      },
      borderRadius: "7px",
      padding: "3px",
      minHeight: "40px",
      boxShadow: "none",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#6c757d",
    }),
    input: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
  };

  if (
    !userInfo?.role?.slug ||
    (!["ADMIN", "SUPER_ADMIN"].includes(userInfo.role.slug) &&
      !moduleAccess?.includes("MOD_MARKETPLACE"))
  ) {
    return <AccessDeniedView />;
  }

  return (
    <div id="content_wrapper">
      <section className="crm-wrapper bg-white pb-5">
        <CommonHeader activeOption="Settings" />

        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="container-fluid px-lg-5 pt-4">
            <div className="p-3 p-lg-4 border border-gray-300 rounded-10 shadow-sm">
              <h3>{t("General Settings")}</h3>

              {/* store name */}
              <div className="form-group mb-4">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-2 mb-lg-0">
                    <label htmlFor="storeName" className="fs-md fw-semibold">
                      {t("Store Name")} <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <input
                      id="storeName"
                      type="text"
                      className={`form-control fs-sm shadow-none ${
                        errors.storeName ? "is-invalid" : "border-gray-300"
                      }`}
                      placeholder="Enter the name of your online store"
                      value={storeName}
                      onChange={(e) => {
                        setStoreName(e.target.value);
                        if (errors.storeName)
                          setErrors((prev) => ({ ...prev, storeName: "" }));
                      }}
                    />
                    {errors.storeName && (
                      <div className="invalid-feedback">{errors.storeName}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* email */}
              <div className="form-group mb-4">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-2 mb-lg-0">
                    <label htmlFor="contactEmail" className="fs-md fw-semibold">
                      {t("Contact Email")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <input
                      id="contactEmail"
                      type="email"
                      className={`form-control fs-sm shadow-none ${
                        errors.contactEmail ? "is-invalid" : "border-gray-300"
                      }`}
                      placeholder="Enter a valid email for customer inquiries"
                      value={contactEmail}
                      onChange={(e) => {
                        setContactEmail(e.target.value);
                        if (errors.contactEmail)
                          setErrors((prev) => ({ ...prev, contactEmail: "" }));
                      }}
                    />
                    {errors.contactEmail && (
                      <div className="invalid-feedback">
                        {errors.contactEmail}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* contact number */}
              <div className="form-group mb-4">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-2 mb-lg-0">
                    <label
                      htmlFor="contactNumber"
                      className="fs-md fw-semibold"
                    >
                      {t("Customer care number")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <input
                      id="contactNumber"
                      type="tel"
                      className={`form-control fs-sm shadow-none ${
                        errors.contactNumber ? "is-invalid" : "border-gray-300"
                      }`}
                      placeholder="Enter a phone number for customer support"
                      value={contactNumber}
                      onChange={(e) => {
                        setContactNumber(e.target.value);
                        if (errors.contactNumber)
                          setErrors((prev) => ({ ...prev, contactNumber: "" }));
                      }}
                    />
                    {errors.contactNumber && (
                      <div className="invalid-feedback">
                        {errors.contactNumber}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Currency */}
              <div className="form-group mb-4">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-2 mb-lg-0">
                    <label className="fs-md fw-semibold">
                      {t("Currency")} <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <Select
                      isMulti
                      options={currencyOptions}
                      value={currencyValue}
                      onChange={currencySelectionHandler}
                      placeholder="Select supported currencies..."
                      classNamePrefix="react-select"
                      styles={currencyStyle}
                    />
                    {errors.currency && (
                      <div className="invalid-feedback d-block">
                        {errors.currency}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* language */}
              <div className="form-group mb-4">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-2 mb-lg-0">
                    <label className="fs-md fw-semibold">
                      {t("Language")} <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <Select
                      isMulti
                      options={languageOptions}
                      value={languageValue}
                      onChange={languageSelectionHandler}
                      placeholder="Select supported languages..."
                      classNamePrefix="react-select"
                      styles={languageStyle}
                    />
                    {errors.language && (
                      <div className="invalid-feedback d-block">
                        {errors.language}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <hr />

              {/* payment methods */}
              <h3>
                {t("Payment Methods")} <span className="text-danger">*</span>
              </h3>
              <div className="form-group mb-2">
                <div className="row">
                  {paymentOptions.map((item, index) => (
                    <div className="col-lg-4 mb-3" key={index}>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={item.value}
                          id={`payment-${item.value}`}
                          onChange={paymentSelectionHandler}
                          checked={selectedPaymentMethods.includes(item.value)}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor={`payment-${item.value}`}
                        >
                          {item.label}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.paymentMethods && (
                  <div className="text-danger mt-2 small">
                    {errors.paymentMethods}
                  </div>
                )}
              </div>

              <hr />

              {/* Warehouses */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>
                  {t("Warehouses")} <span className="text-danger">*</span>
                </h3>
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#saveAddress"
                  className="btn btn-outline-primary d-flex align-items-center gap-1"
                >
                  <span className="d-block material-symbols-outlined">add</span>
                  <span className="d-block">Add New Address</span>
                </Link>
              </div>

              {errors.addressBlock && (
                <div className="alert alert-danger">{errors.addressBlock}</div>
              )}

              <div
                className={
                  addressBlock.length > 0
                    ? "fields d-flex flex-column gap-3 mb-4"
                    : "d-none"
                }
              >
                {addressBlock.map((item, index) => (
                  <div
                    className="field rounded-5 border border-gray-300 p-3"
                    key={index}
                  >
                    <div className="d-flex justify-content-between gap-3 mb-3">
                      <p className="fw-bold fs-md">{item.name}</p>
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <Link
                          to="#"
                          data-bs-toggle="modal"
                          data-bs-target="#saveAddress"
                          onClick={() => updateAddressBlock(item, index)}
                        >
                          <span className="material-symbols-outlined">
                            edit
                          </span>
                        </Link>
                        <Link
                          to="#"
                          onClick={() => onDeleteAddressBlock(index, item)}
                        >
                          <span className="material-symbols-outlined text-danger">
                            delete
                          </span>
                        </Link>
                      </div>
                    </div>
                    <p className="mb-1">
                      <strong className="fw-semibold">Address:</strong>{" "}
                      {item.address}
                    </p>
                    <p className="mb-1">
                      <strong className="fw-semibold">Location:</strong>{" "}
                      {`${item.city}, ${item.state}, ${item.country} - ${item.zipcode}`}
                    </p>
                    {item.mapurl && (
                      <p className="mb-0">
                        <strong className="fw-semibold">Map:</strong>{" "}
                        <a
                          href={item.mapurl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on Map
                        </a>
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* --- Save Button --- */}
              <div className="action d-flex justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  onClick={saveSettingsHandler}
                  disabled={isSaving}
                >
                  {isSaving && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {isSaving ? t("Saving...") : t("Save Settings")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- Modals and Alerts --- */}
        <SaveAddressModal
          addressIndex={addressIndex}
          addressData={addressData}
          setAddressData={setAddressData}
          addressBlock={addressBlock}
          setaddressBlock={setAddressBlock}
          setaddressIndex={setAddressIndex}
        />

        {showAlert && (
          <AlertNotification
            showAlert={showAlert}
            message={alertMessage}
            alertType={messageType}
            onClose={onAlertClose}
          />
        )}
      </section>
    </div>
  );
};

export default SettingsBody;
